import type { Submission } from '../../api/authoring'

type Status = Submission['status']

/**
 * What a status means to a site's owner, in their words rather than the
 * platform's.
 *
 * `spam` and `trapped` are two mechanisms and one outcome — nobody was
 * emailed — but they are not collapsed into one label, because the
 * reader's next question differs: a filtered submission is a judgement
 * somebody can disagree with, and a trapped one is very often a browser
 * autofilling a hidden field for a real person.
 */
export function statusLabel(status: Status): string {
  switch (status) {
    case 'sent':
      return 'Emailed to you'
    case 'accepted':
      return 'Waiting to be emailed'
    case 'failed':
      return 'Could not be emailed'
    case 'spam':
      return 'Filtered as spam'
    case 'trapped':
      return 'Caught by the hidden field'
    default:
      return status
  }
}

/** Why a reader should care, as a colour. */
export function statusColour(status: Status): 'success' | 'default' | 'error' | 'warning' {
  switch (status) {
    case 'sent':
      return 'success'
    // A failure is the one status a customer has to act on: somebody
    // contacted them and the email did not arrive.
    case 'failed':
      return 'error'
    case 'spam':
    case 'trapped':
      return 'warning'
    default:
      return 'default'
  }
}

/** Whether the platform ever delivered this one. */
export function wasDelivered(status: Status): boolean {
  return status === 'sent'
}

/**
 * Whether this submission can be sent.
 *
 * A discarded submission's answers were cleared by design, so the screen
 * says so rather than offering a button that fails.
 */
export function canResend(submission: Submission): boolean {
  if (Object.keys(submission.values ?? {}).length === 0) return false
  return submission.status === 'spam' || submission.status === 'trapped' || submission.status === 'failed'
}

/** A timestamp as a reader reads one. */
export function when(stamp: string): string {
  const at = new Date(stamp)
  if (Number.isNaN(at.getTime())) return stamp
  return at.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  })
}
